// import { LoadingState } from '@powerplay/core-minigames-ui-ssm'
import {
  corePhasesManager,
  PlayersSortTypes,
  settings,
  SettingsTypes,
  timeManager,
  type PlayerInfoForTable,
  gsap,
  requestManager,
  modes,
  CustomEvents,
  CorePhases,
  PhaseFinalResultsManager
} from '@powerplay/core-minigames'
import { disciplinePhasesManager } from '../phases/DisciplinePhasesManager'
import { DisciplinePhases } from '../types'
import { player } from '../entities/athlete/player'
import {
  timeState,
  directionsState,
  attemptsTableState
} from '@/stores'
import {
  fpsLookerState,
  gameSettingsState,
  waitingState
} from '@powerplay/core-minigames-ui-ssm'
import type { MiniTableRowData } from '@powerplay/core-minigames-ui-ssm/ssm-dist/src/ui/common/MiniTable/MiniTable.vue'
import { playersManager } from '@powerplay/core-minigames'
import { getActivePinia } from 'pinia'

export interface FpsDataVuex {
  averageFps: number
  actualFps: number
  actualAverageFps: number
}

export interface SpeedData {
  visible: boolean
  speed: string | number
}

export class StateManager {

  private tweenAttemptsTable?: gsap.core.Tween

  public getFpsStarted(): boolean {

    return fpsLookerState().isStarted ?? false

  }

  public setFpsData(fpsData: FpsDataVuex): void {

    fpsLookerState().$patch(fpsData)

  }

  public allowDirectionState(): void {

    directionsState().show = true

  }

  public setBeforeGameStartPhase(): void {

    gameSettingsState().$patch({
      graphicsSettings: settings.getSetting(SettingsTypes.quality),
      graphicsAuto: settings.getSetting(SettingsTypes.qualityAuto) === 1,
      volume: settings.getSetting(SettingsTypes.sounds) === 1,
      isLeft: settings.getSetting(SettingsTypes.isLeft) === 1,
      controlsType: settings.getSetting(SettingsTypes.controlsType)
    })

    attemptsTableState().playerInfo = {
      country: playersManager.getPlayer().country,
      countryString: playersManager.getPlayer().countryString ?? '',
      name: playersManager.getPlayer().name
    }

  }

  public setUpdateTimeState(): void {

    let time = '0.0'
    if (disciplinePhasesManager.actualPhase >= DisciplinePhases.finish) {

      time = timeManager.getTimeInFormatFromSeconds(Math.ceil(player.throwFinalLength * 100) / 100)

    } else {

      time = timeManager.getGameTimeWithPenaltyInFormat(1)

    }

    timeState().time = time


  }

  public showAttemptsTable(): void {

    if (modes.isTutorial()) return
    const bestResult = playersManager.getBestResult(PlayersSortTypes.descending, undefined, true)
    let bestResultMarked = false

    playersManager.getPlayer().resultsArr?.forEach((result, index) => {

      let resultValue = result.main.toFixed(2)
      if (result.second === 1) resultValue = 'X'
      if (result.main === -1) resultValue = ''
      attemptsTableState().attempts[index].value = resultValue
      attemptsTableState().attempts[index].highlight = false

      if (!bestResultMarked && result.main === bestResult && bestResult !== 0) {

        bestResultMarked = true
        attemptsTableState().attempts[index].highlight = true

      }

    })

    playersManager.setStandings(2)
    attemptsTableState().position = playersManager.getPlayerActualPosition()
    attemptsTableState().visible = !modes.isTutorial() && !modes.isTrainingMode()

    this.tweenAttemptsTable = gsap.to({}, {
      duration: 3,
      onComplete: () => {

        this.hideAttemptsTable()

      }
    })

  }

  public hideAttemptsTable(): void {

    this.tweenAttemptsTable?.kill()
    attemptsTableState().visible = false

  }

  /**
   * Vytiahnutie dat pre minitable
   * @param playerPoints - body posledneho pokusu hraca
   */
  public static getDataForMiniTable(playerPoints = 0): MiniTableRowData[] {

    const rowsData = [] as MiniTableRowData[]

    // pre minitabulku je potrebne ine zobrazenie (nedavame NM)
    playersManager.setStandings(2, undefined, undefined, true)
    const standings = playersManager.getStandings()
    playersManager.setStandings(2)
    const playerPosition = standings.filter((value) => value.playable)[0].position

    let indexes = []

    if (playerPosition < 4) {

      indexes = [0, 1, 2, 3]

    } else if (playerPosition === standings.length) {

      indexes = [0, playerPosition - 3, playerPosition - 2, playerPosition - 1]

    } else {

      indexes = [0, playerPosition - 2, playerPosition - 1, playerPosition]

    }

    indexes.forEach((index) => {


      if (standings[index]) {

        const minitableRowData = this.getMiniTableDataFromPlayerInfo(standings[index], playerPoints)
        if (minitableRowData.textRight !== '') rowsData.push(minitableRowData)

      }


    })
    return rowsData

  }

  /**
   * Namapovanie dat pre minitable
   * @param playerInfo - playerInfo pre mapovanie
   * @param playerPoints - body posledneho pokusu hraca
   * @returns MiniTableRowData
   */
  private static getMiniTableDataFromPlayerInfo(playerInfo: PlayerInfoForTable, playerPoints: number) {

    let lastAttempt = playerPoints.toFixed(2)
    if (playerInfo.uuid !== player.uuid) {

      const index = corePhasesManager.disciplineActualAttempt - 1
      lastAttempt = playersManager.getPlayerById(playerInfo.uuid)?.resultsArr?.[index]?.main.toFixed(2) ?? ''

    }

    if (lastAttempt === '0.00') lastAttempt = ' X '

    return {
      position: playerInfo.position,
      country: playerInfo.country,
      countryString: playerInfo.countryString,
      player: {
        name: playerInfo.name,
        isPlayer: playerInfo.playable
      },
      textRight: String(playerInfo.result),
      lastAttempt: corePhasesManager.disciplineActualAttempt === 1 ? '' : lastAttempt
    } as MiniTableRowData

  }

  /**
   * Porobenie veci, ked dobehnu vsetky potrebne requesty
   */
  public afterAllRequestsDone(): void {

    const isWaiting = requestManager.isFirstTrainingTutorial()
    const trainingButtonsDisabled = requestManager.isFirstTrainingTutorial()
    waitingState().$patch({
      isWaiting,
      trainingButtonsDisabled
    })

    // v tutoriali hned presmerujeme
    if (modes.isTutorial()) {

      window.dispatchEvent(new CustomEvent(CustomEvents.finalRedirectDone))
      requestManager.redirect()

    }

    const finalPhase = corePhasesManager.getPhase(CorePhases.finalResults) as PhaseFinalResultsManager
    if (finalPhase) finalPhase.enableSkip()

  }

  /**
   * Resetovanie pinie
   * @param specialExceptions - Specialne vynimky navyse
   */
  public resetPinia(specialExceptions: string[] = []): void {

    const exceptions = ['tutorialState', 'inputsState', 'mobileState', 'loadingState', 'gameSettingsState']
    if (specialExceptions.length > 0) exceptions.push(...specialExceptions)

    // eslint-disable-next-line
    // @ts-ignore
    getActivePinia()?._s.forEach((s: Store) => {

      if (!exceptions.includes(s.$id)) s.$reset()

    })

  }

}

export const stateManager = new StateManager()
